/* eslint-disable react/jsx-props-no-spreading */
import { useSpring, animated } from '@react-spring/web';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDrag } from 'react-use-gesture';

import { useResizeObserver } from '../../hooks/useObserver';
import * as AppPropTypes from '../../lib/PropTypes';
import { getComponentFromName } from '../../lib/utils';

import PillButton from '../buttons/Pill';
import Arrow from '../icons/Arrow';
import CircleTimerIcon from '../icons/CircleTimer';
import * as Slides from '../slides';

import styles from '../../../styles/headers/carousel-2022.module.scss';

const GESTURE_SENSITIVITY = 0.5;
const VELOCITY_THRESHOLD = 0.3;
const DISTANCE_RATIO_THRESHOLD = 0.5;

const propTypes = {
    description: PropTypes.string,
    slides: AppPropTypes.slides,
    className: PropTypes.string,
    delay: PropTypes.number,
    autoplay: PropTypes.bool,
};

const defaultProps = {
    description: null,
    slides: null,
    className: null,
    delay: 6000,
    autoplay: true,
};

const CarouselHeader = ({ description, slides, delay, autoplay: initialAutoplay, className }) => {
    const [autoplay, setAutoplay] = useState(initialAutoplay);
    const timer = useRef(0);
    const finalSlides = useMemo(
        () => [
            {
                type: 'home',
                description,
            },
            ...(slides || []),
        ],
        [slides],
    );
    const slidesLength = finalSlides.length;
    const hasMultipleSlides = slidesLength > 1;

    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentX, setCurrentX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const {
        ref: carouselRef,
        entry: { contentRect: carouselRect },
    } = useResizeObserver();
    const { width: carouselWidth = 0 } = carouselRect || {};
    const currentSlide = currentIndex < finalSlides.length ? finalSlides[currentIndex] : null;
    const { type: slideType = null } = currentSlide || {};

    const [animatedStyles, api] = useSpring(() => ({
        x: 0,
        config: { tension: 250, friction: 35 },
    }));

    const nextSlide = () => {
        setCurrentIndex(currentIndex + 1 >= slidesLength ? 0 : currentIndex + 1);
    };

    const previousSlide = () => {
        setCurrentIndex(currentIndex - 1 < 0 ? slidesLength - 1 : currentIndex - 1);
    };

    const onGestureEnd = (mx, velocity, direction) => {
        // gesture is not "horizontal enough"
        if (Math.abs(direction[0]) < 0.5) {
            api.start({ x: currentX });
            return;
        }

        if (
            (mx < 0 && velocity > VELOCITY_THRESHOLD) ||
            mx <= carouselWidth * DISTANCE_RATIO_THRESHOLD * -1
        ) {
            setAutoplay(false);
            nextSlide();
            return;
        }
        if (
            (mx > 0 && velocity > VELOCITY_THRESHOLD) ||
            mx >= carouselWidth * DISTANCE_RATIO_THRESHOLD
        ) {
            setAutoplay(false);
            previousSlide();
            return;
        }

        api.start({ x: currentX });
    };

    const bind = useDrag(({ event, active, velocity, direction, movement: [mx] }) => {
        if (event.pointerType !== 'touch') return;

        if (!active) {
            onGestureEnd(mx, velocity, direction);
        } else {
            api.start({
                x: currentX + mx * GESTURE_SENSITIVITY,
                immediate: true,
            });
        }
        setIsDragging(active);
    });

    const onClickPrevious = useCallback(() => {
        setAutoplay(false);
        previousSlide();
    }, [currentIndex, slidesLength]);

    const onClickNext = useCallback(() => {
        setAutoplay(false);
        nextSlide();
    }, [currentIndex, slidesLength]);

    const onClickDot = useCallback(
        (index) => {
            setAutoplay(false);
            setCurrentIndex(index);
        },
        [currentIndex],
    );

    useEffect(() => {
        const x = currentIndex * carouselWidth * -1;
        setCurrentX(x);
        api.start({ x });
    }, [carouselWidth, setCurrentX, currentIndex]);

    useEffect(() => {
        timer.current = autoplay
            ? setTimeout(() => {
                  nextSlide();
              }, delay)
            : null;

        return () => {
            if (timer.current !== null) {
                clearTimeout(timer.current);
            }
        };
    }, [delay, currentIndex, autoplay]);

    return (
        <div
            {...(hasMultipleSlides ? bind() : null)}
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles[`${slideType}Type`]]: slideType !== null,
                },
            ])}
        >
            <animated.div style={animatedStyles} ref={carouselRef} className={styles.track}>
                {finalSlides.map((item, index) => {
                    const { type } = item;
                    const SlideComponent = getComponentFromName(Slides, type);
                    const isCurrent = index === currentIndex;
                    return (
                        <SlideComponent
                            key={`slide-${index + 1}`}
                            className={classNames(styles.slide, {
                                [styles.isHome]: type === 'home',
                                [styles.isCurrent]: isCurrent,
                                [styles.isDragging]: isCurrent && isDragging,
                            })}
                            isCurrent={isCurrent}
                            item={item}
                        />
                    );
                })}
            </animated.div>

            {hasMultipleSlides ? (
                <PillButton
                    className={classNames(styles.navButton, styles.previous)}
                    onClick={onClickPrevious}
                    icon={<Arrow className={styles.arrow} direction="left" />}
                    isDark
                />
            ) : null}

            {hasMultipleSlides ? (
                <PillButton
                    className={classNames(styles.navButton, styles.next)}
                    onClick={onClickNext}
                    icon={<Arrow className={styles.arrow} direction="right" />}
                    isDark
                />
            ) : null}

            {hasMultipleSlides ? (
                <div className={styles.dots}>
                    {finalSlides.map((item, index) => (
                        <button
                            key={`${item.type}-${index + 1}`}
                            className={classNames(styles.dot, {
                                [styles.isCurrent]: index === currentIndex,
                                [styles.isAutoplay]: autoplay,
                            })}
                            type="button"
                            onClick={() => onClickDot(index)}
                        >
                            {index + 1}
                            <CircleTimerIcon className={styles.timer} />
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

CarouselHeader.propTypes = propTypes;
CarouselHeader.defaultProps = defaultProps;

export default CarouselHeader;
